import type { FlatLessonStep, LessonExercise, PracticeLesson } from '@/types/practiceLesson'
import { isAccepted, matchPrimary, normalizeAnswer } from '@/lib/answerMatch'

const SUPPORTED_EXERCISE_TYPES = new Set<LessonExercise['type']>(['fill_blank', 'multiple_choice'])

export function flattenLesson(lesson: PracticeLesson): FlatLessonStep[] {
  const steps: FlatLessonStep[] = []
  let stepIndex = 0
  for (const section of lesson.sections) {
    for (const exercise of section.exercises) {
      if (!SUPPORTED_EXERCISE_TYPES.has(exercise.type)) {
        continue
      }
      steps.push({
        sectionId: section.id,
        sectionTitle: section.title,
        exercise,
        stepIndex,
      })
      stepIndex += 1
    }
  }
  return steps
}

export { normalizeAnswer }

export function isExerciseCorrect(exercise: LessonExercise, answer: string): boolean {
  const user = normalizeAnswer(answer)
  const expected = normalizeAnswer(exercise.correctAnswer)
  if (user === expected) {
    return true
  }
  const optionBased =
    exercise.type === 'multiple_choice'
    || (exercise.type === 'fill_blank' && (exercise.options?.length ?? 0) > 0)
  if (exercise.type === 'fill_blank' && !optionBased) {
    return isAccepted(matchPrimary(answer, exercise.correctAnswer))
  }
  if (optionBased) {
    const matchedOption = exercise.options.find((opt) => normalizeAnswer(opt) === user)
    if (matchedOption && normalizeAnswer(matchedOption) === expected) {
      return true
    }
    const letterMatch = user.match(/^([a-d])[).:\s]/i)
    if (letterMatch) {
      const index = letterMatch[1].toUpperCase().charCodeAt(0) - 65
      const option = exercise.options[index]
      return option != null && normalizeAnswer(option) === expected
    }
  }
  return false
}

export function parsePracticeLesson(raw: unknown): PracticeLesson | null {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const lesson = raw as PracticeLesson
  if (!Array.isArray(lesson.sections) || lesson.sections.length === 0) {
    return null
  }
  const hasExercises = lesson.sections.some((s) => s.exercises?.length > 0)
  return hasExercises ? lesson : null
}
